export default class CoverSelector {
  constructor(container = null) {
    // Usar window.COVER_CONFIG si está disponible
    const coverConfig = window.COVER_CONFIG || {}
    this.covers = coverConfig.covers || []
    if (!this.covers.length) {
      console.warn('CoverSelector: No covers found in COVER_CONFIG')
      return
    }

    // Elementos del DOM
    this.paletteContainer = document.getElementById('cover-background-palette')
    this.bgInput = document.getElementById('cover-background-color')
    this.fontSelect = document.getElementById('cover-typography')
    
    // Elementos SVG de la previsualización
    this.svgCoverDetails = document.getElementById('svg-cover-details')
    this.leftCoverRect = document.getElementById('cover-left-sheet-background-rect')
    this.rightCoverRect = document.getElementById('cover-right-sheet-background-rect')
    this.spineRect = document.getElementById('cover-spine-background-rect')
    this.leftCoverTitleText = document.getElementById('cover-left-sheet-title-text')
    this.leftCoverSubtitleText = document.getElementById('cover-left-sheet-subtitle-text')
    this.spineText = document.getElementById('cover-spine-text')
    // La contraportada no tiene elementos de texto editables, solo el logo

    // Inputs de texto
    this.titleInput = document.getElementById('cover-title')
    this.subtitleInput = document.getElementById('cover-subtitle')
    this.spineInput = document.getElementById('cover-spine-text-input')

    // Configuración del fotobook para cálculos de tamaño
    this.fotobookConfig = window.FOTOBOOK_CONFIG || {}
    this.pageWidth = this.fotobookConfig.pageWidth || 300
    this.pageHeight = this.fotobookConfig.pageHeight || 300

    // Cover por defecto
    this.currentCoverId = coverConfig.defaultCoverId || this.covers[0]?.id

    // Inicializar
    if (this.currentCoverId) {
      this.selectCover(this.currentCoverId)
    }
    this.attachEvents()
    this.initializeCharCounters()
    // Actualizar posición del texto del lomo después de inicializar
    setTimeout(() => {
      this.updateSpineTextPosition()
    }, 100)
  }

  attachEvents() {
    // Selección de color de fondo desde la paleta
    if (this.paletteContainer) {
      this.paletteContainer.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-color]')
        if (!chip) return
        const color = chip.getAttribute('data-color')
        this.setBackgroundColor(color)
        this.markActiveChip(color)
      })
    }

    // Cambio de tipografía
    if (this.fontSelect) {
      this.fontSelect.addEventListener('change', (e) => {
        const font = e.target.value
        this.applyFontToPreview(font)
      })
    }

    // Actualizar previsualización cuando cambia el título
    if (this.titleInput) {
      this.titleInput.addEventListener('input', (e) => {
        // Re-buscar el elemento del SVG por si se recreó
        this.leftCoverTitleText = document.getElementById('cover-left-sheet-title-text')
        this.updateTitlePreview(e.target.value)
      })
    }

    // Actualizar previsualización cuando cambia el subtítulo
    if (this.subtitleInput) {
      this.subtitleInput.addEventListener('input', (e) => {
        // Re-buscar el elemento del SVG por si se recreó
        this.leftCoverSubtitleText = document.getElementById('cover-left-sheet-subtitle-text')
        this.updateSubtitlePreview(e.target.value)
      })
    }

    // Actualizar previsualización cuando cambia el texto del lomo
    if (this.spineInput) {
      this.spineInput.addEventListener('input', (e) => {
        // Re-buscar el elemento del SVG por si se recreó
        this.spineText = document.getElementById('cover-spine-text')
        this.updateSpinePreview(e.target.value)
      })
    }

    // Contador de caracteres para inputs con clase .count-chars
    document.addEventListener('keyup', (e) => {
      if (e.target.matches('.count-chars')) {
        this.updateCharCounter(e.target)
      }
    })
  }

  /**
   * Actualiza el contador de caracteres para un input dado
   * @param {HTMLElement} input - El elemento input a actualizar
   */
  updateCharCounter(input) {
    const targetSelector = input.getAttribute('data-target')
    if (targetSelector) {
      const targetElement = document.querySelector(targetSelector)
      if (targetElement) {
        const currentLength = input.value.length
        const maxLength = input.getAttribute('maxlength') || 0
        targetElement.textContent = `${currentLength}/${maxLength}`
      }
    }
  }

  /**
   * Inicializa los contadores de caracteres para todos los inputs con clase .count-chars
   */
  initializeCharCounters() {
    const countCharInputs = document.querySelectorAll('.count-chars')
    countCharInputs.forEach((input) => {
      this.updateCharCounter(input)
    })
  }

  selectCover(coverId) {
    const cover = this.covers.find(c => String(c.id) === String(coverId)) || this.covers[0]
    if (!cover) return
    this.currentCoverId = cover.id

    // Actualizar paleta y fuentes
    this.updatePalette(cover)
    this.updateFonts(cover)

    // Actualizar inputs con valores del cover
    if (this.titleInput && cover.title) {
      this.titleInput.value = cover.title
      this.updateCharCounter(this.titleInput)
    }
    if (this.subtitleInput && cover.subtitle) {
      this.subtitleInput.value = cover.subtitle
      this.updateCharCounter(this.subtitleInput)
    }
    if (this.spineInput) {
      // El texto del lomo se actualiza desde el input del usuario
      this.updateCharCounter(this.spineInput)
    }

    // Actualizar previsualización completa del cover
    this.updateCoverPreview(cover)
    
    // Actualizar posición del texto del lomo y sincronizar el valor del input
    if (this.spineInput && this.spineText) {
      // Si el input tiene un valor, actualizar el SVG
      if (this.spineInput.value) {
        this.updateSpinePreview(this.spineInput.value)
      } else {
        // Si no, solo actualizar la posición
        this.updateSpineTextPosition()
      }
    } else {
      // Asegurarse de que las referencias existan
      this.spineText = document.getElementById('cover-spine-text')
      if (this.spineText) {
        this.updateSpineTextPosition()
      }
    }

    // Notificar a FotobookEditor si existe
    if (window.fotobookEditor && window.fotobookEditor.selectCoverFormat) {
      // Buscar el elemento del formato de cover correspondiente
      const coverFormatOption = document.querySelector(`[data-cover-id="${coverId}"].cover-format-option`)
      if (coverFormatOption) {
        window.fotobookEditor.selectCoverFormat(coverFormatOption)
      }
    }
  }

  updatePalette(cover) {
    const colors = this.parseColors(cover.palette)
    if (!this.paletteContainer) return

    this.paletteContainer.innerHTML = ''
    const palette = colors.length ? colors : ['#ffffff']

    palette.forEach((color, idx) => {
      const chip = document.createElement('button')
      chip.type = 'button'
      chip.className = 'palette-color-btn-fotobook'
      chip.style.background = this.hexToRgbCss(color)
      chip.style.color = this.getContrastColor(color)
      chip.style.padding = '.25em'
      chip.setAttribute('data-color', color)
      chip.setAttribute('title', color)

      const text = document.createElement('span')
      text.textContent = color;
      text.classList.add('sr-only');
      chip.appendChild(text);

      this.paletteContainer.appendChild(chip)

      if (idx === 0) {
        this.setBackgroundColor(color)
        chip.classList.add('active')
      }
    })
  }

  updateFonts(cover) {
    if (!this.fontSelect) return
    const fonts = this.parseFonts(cover.font_families)

    this.fontSelect.innerHTML = ''
    fonts.forEach((font, idx) => {
      const opt = document.createElement('option')
      opt.value = font
      opt.textContent = font
      opt.style.fontFamily = `'${font}', sans-serif`
      this.fontSelect.appendChild(opt)
      if (idx === 0) {
        this.fontSelect.value = font
        this.applyFontToPreview(font)
      }
    })
  }

  setBackgroundColor(color) {
    if (this.bgInput) {
      this.bgInput.value = color
    }
    // Actualizar rects de fondo en la previsualización
    if (this.leftCoverRect) {
      this.leftCoverRect.setAttribute('fill', color)
    }
    if (this.rightCoverRect) {
      this.rightCoverRect.setAttribute('fill', color)
    }
    // Actualizar color del lomo también
    if (this.spineRect) {
      this.spineRect.setAttribute('fill', color)
    }
  }

  applyFontToPreview(font) {
    const fontValue = font ? `'${font}', sans-serif` : null
    if (fontValue) {
      // Aplicar fuente a los elementos de texto editables
      const textElements = [
        this.leftCoverTitleText,
        this.leftCoverSubtitleText,
        this.spineText
      ]
      textElements.forEach(node => {
        if (node) node.setAttribute('font-family', fontValue)
      })
      
      // Recalcular el tamaño del título después de cambiar la tipografía
      // ya que diferentes tipografías tienen diferentes anchos
      if (this.leftCoverTitleText) {
        const cover = this.covers.find(c => String(c.id) === String(this.currentCoverId))
        if (cover) {
          const svgWidth = this.pageWidth
          const svgHeight = this.pageHeight
          const titleSizeFromCMS = (cover.title_size || 0.08) * svgHeight
          const titleSizeAdjusted = this.calculateTitleSizeForSafeArea(
            this.leftCoverTitleText,
            svgWidth,
            titleSizeFromCMS,
            cover.title_align || 'center'
          )
          const finalTitleSize = Math.min(titleSizeFromCMS, titleSizeAdjusted)
          this.leftCoverTitleText.setAttribute('font-size', finalTitleSize)
        }
      }
    }
  }

  updateCoverPreview(cover) {
    // Renderizar toda la estructura del cover (textos y fotos) en un solo grupo
    // Esto asegura que todo el contenido esté junto y con coordenadas limpias
    if (this.svgCoverDetails) {
      this.renderCoverDetails(cover)
    }

    // Actualizar título y subtítulo si el usuario los modifica
    if (cover.title) {
      this.updateTitlePreview(cover.title)
    }

    if (cover.subtitle) {
      this.updateSubtitlePreview(cover.subtitle)
    }

    // Actualizar color de fondo si hay paleta
    if (cover.palette) {
      const colors = this.parseColors(cover.palette)
      if (colors.length > 0) {
        this.setBackgroundColor(colors[0])
      }
    }

    // Actualizar posiciones y tamaños del texto según el cover
    // (Los textos ya se crearon con las posiciones correctas en renderCoverDetails)
    this.updateTextPositions(cover)
  }

  renderCoverDetails(cover) {
    if (!this.svgCoverDetails) return

    // Buscar el grupo de la portada (cover-left-sheet-group) que ahora está a la derecha
    const coverGroup = this.svgCoverDetails.querySelector('#cover-left-sheet-group')
    if (!coverGroup) return

    // Obtener el grupo de contenido único dentro del grupo de la portada
    let contentGroup = coverGroup.querySelector('#cover-content-group')
    if (!contentGroup) {
      contentGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
      contentGroup.setAttribute('class', 'cover-sheet-content')
      contentGroup.setAttribute('id', 'cover-content-group')
      coverGroup.appendChild(contentGroup)
    } else {
      // Limpiar todo el contenido previo (formas y textos) para renderizar desde cero
      contentGroup.innerHTML = ''
      // Resetear referencias a elementos de texto
      this.leftCoverTitleText = null
      this.leftCoverSubtitleText = null
    }

    // Crear los elementos de texto con coordenadas absolutas limpias
    this.ensureTextElements(contentGroup, cover)

    // Renderizar layout de fotos si existe (también con coordenadas absolutas)
    if (cover.data) {
      this.renderCoverLayout(contentGroup, cover.data)
    }
  }

  ensureTextElements(contentGroup, cover = null) {
    const svgWidth = this.pageWidth
    const svgHeight = this.pageHeight

    // Usar valores del cover si están disponibles, sino valores por defecto
    const titleText = cover?.title || 'Título de Portada'
    const subtitleText = cover?.subtitle || 'Subtítulo'
    const titleY = cover?.title_y ? (cover.title_y * svgHeight) : (svgHeight * 0.4)
    const subtitleY = cover?.subtitle_y ? (cover.subtitle_y * svgHeight) : (svgHeight * 0.55)
    
    // Calcular tamaño inicial del título basado en title_size del CMS
    const titleSizeFromCMS = cover?.title_size ? (cover.title_size * svgHeight) : (svgHeight * 0.08)
    
    const subtitleSize = cover?.subtitle_size ? (cover.subtitle_size * svgHeight) : (svgHeight * 0.06)
    const titleAlign = cover?.title_align || 'center'
    const subtitleAlign = cover?.subtitle_align || 'center'
    const titleX = this.calculateTextX(titleAlign, svgWidth)
    const subtitleX = this.calculateTextX(subtitleAlign, svgWidth)
    const titleAnchor = this.getTextAnchor(titleAlign)
    const subtitleAnchor = this.getTextAnchor(subtitleAlign)

    // Obtener la fuente del cover si está disponible
    const fontFamily = cover?.font_families 
      ? `'${cover.font_families.split(',')[0].trim()}', sans-serif`
      : 'Arial, sans-serif'

    // Crear el elemento de título con coordenadas absolutas limpias
    const titleElement = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    titleElement.setAttribute('id', 'cover-left-sheet-title-text')
    titleElement.setAttribute('x', titleX)
    titleElement.setAttribute('y', titleY)
    titleElement.setAttribute('text-anchor', titleAnchor)
    titleElement.setAttribute('dominant-baseline', 'middle')
    titleElement.setAttribute('font-size', titleSizeFromCMS)
    titleElement.setAttribute('fill', '#FFF')
    titleElement.setAttribute('font-weight', 'bold')
    titleElement.setAttribute('font-family', fontFamily)
    titleElement.textContent = titleText
    contentGroup.appendChild(titleElement)
    this.leftCoverTitleText = titleElement

    // Calcular tamaño ajustado al safe area después de crear el elemento
    const titleSizeAdjusted = this.calculateTitleSizeForSafeArea(
      titleElement,
      svgWidth,
      titleSizeFromCMS,
      titleAlign
    )
    
    // Aplicar el tamaño ajustado (el menor entre CMS y safe area)
    const finalTitleSize = Math.min(titleSizeFromCMS, titleSizeAdjusted)
    titleElement.setAttribute('font-size', finalTitleSize)

    // Crear el elemento de subtítulo con coordenadas absolutas limpias
    const subtitleElement = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    subtitleElement.setAttribute('id', 'cover-left-sheet-subtitle-text')
    subtitleElement.setAttribute('x', subtitleX)
    subtitleElement.setAttribute('y', subtitleY)
    subtitleElement.setAttribute('text-anchor', subtitleAnchor)
    subtitleElement.setAttribute('dominant-baseline', 'middle')
    subtitleElement.setAttribute('font-size', subtitleSize)
    subtitleElement.setAttribute('fill', '#374151')
    subtitleElement.setAttribute('font-family', fontFamily)
    subtitleElement.textContent = subtitleText
    contentGroup.appendChild(subtitleElement)
    this.leftCoverSubtitleText = subtitleElement

    // Calcular tamaño ajustado al safe area después de crear el elemento
    const subtitleSizeAdjusted = this.calculateSubtitleSizeForSafeArea(
      subtitleElement,
      svgWidth,
      subtitleSize,
      subtitleAlign
    )
    
    // Aplicar el tamaño ajustado (el menor entre CMS y safe area)
    const finalSubtitleSize = Math.min(subtitleSize, subtitleSizeAdjusted)
    subtitleElement.setAttribute('font-size', finalSubtitleSize)
  }

  renderCoverLayout(contentGroup, data) {
    if (!data || !this.svgCoverDetails) return

    const svgWidth = this.pageWidth
    const svgHeight = this.pageHeight
    const shapes = data.split('|')

    shapes.forEach((shapeData, index) => {
      const shapeMatch = shapeData.trim().match(/^([rc]):([\d.]+),([\d.]+),([\d.]+),([\d.]+)$/)
      if (!shapeMatch) return

      const [, type, width, height, x, y] = shapeMatch
      const shapeElement = type === 'r' 
        ? document.createElementNS('http://www.w3.org/2000/svg', 'rect')
        : document.createElementNS('http://www.w3.org/2000/svg', 'circle')

      if (type === 'r') {
        shapeElement.setAttribute('x', parseFloat(x) * svgWidth)
        shapeElement.setAttribute('y', parseFloat(y) * svgHeight)
        shapeElement.setAttribute('width', parseFloat(width) * svgWidth)
        shapeElement.setAttribute('height', parseFloat(height) * svgHeight)
      } else {
        const radius = (parseFloat(width) * svgWidth) / 2
        shapeElement.setAttribute('r', radius)
        shapeElement.setAttribute('cx', parseFloat(x) * svgWidth + radius)
        shapeElement.setAttribute('cy', parseFloat(y) * svgHeight + radius)
      }

      shapeElement.setAttribute('fill', 'none')
      shapeElement.setAttribute('stroke', '#55859a')
      shapeElement.setAttribute('stroke-width', '1')
      shapeElement.setAttribute('class', 'cover-photo-slot')
      shapeElement.setAttribute('data-slot-index', index)

      contentGroup.appendChild(shapeElement)
    })
  }

  updateTitlePreview(text) {
    // Asegurarse de que la referencia al elemento existe
    if (!this.leftCoverTitleText) {
      this.leftCoverTitleText = document.getElementById('cover-left-sheet-title-text')
    }
    
    if (this.leftCoverTitleText) {
      this.leftCoverTitleText.textContent = text || 'Título de Portada'
      
      // Recalcular el tamaño después de cambiar el texto para asegurar que quepa en el safe area
      const cover = this.covers.find(c => String(c.id) === String(this.currentCoverId))
      if (cover) {
        const svgWidth = this.pageWidth
        const svgHeight = this.pageHeight
        const titleSizeFromCMS = (cover.title_size || 0.08) * svgHeight
        const titleSizeAdjusted = this.calculateTitleSizeForSafeArea(
          this.leftCoverTitleText,
          svgWidth,
          titleSizeFromCMS,
          cover.title_align || 'center'
        )
        const finalTitleSize = Math.min(titleSizeFromCMS, titleSizeAdjusted)
        this.leftCoverTitleText.setAttribute('font-size', finalTitleSize)
      }
    }
    // La contraportada no tiene texto editable
  }

  updateSubtitlePreview(text) {
    // Asegurarse de que la referencia al elemento existe
    if (!this.leftCoverSubtitleText) {
      this.leftCoverSubtitleText = document.getElementById('cover-left-sheet-subtitle-text')
    }
    
    if (this.leftCoverSubtitleText) {
      this.leftCoverSubtitleText.textContent = text || 'Subtítulo'
      
      // Recalcular el tamaño después de cambiar el texto para asegurar que quepa en el safe area
      const cover = this.covers.find(c => String(c.id) === String(this.currentCoverId))
      if (cover) {
        const svgWidth = this.pageWidth
        const svgHeight = this.pageHeight
        const subtitleSizeFromCMS = (cover.subtitle_size || 0.06) * svgHeight
        const subtitleSizeAdjusted = this.calculateSubtitleSizeForSafeArea(
          this.leftCoverSubtitleText,
          svgWidth,
          subtitleSizeFromCMS,
          cover.subtitle_align || 'center'
        )
        const finalSubtitleSize = Math.min(subtitleSizeFromCMS, subtitleSizeAdjusted)
        this.leftCoverSubtitleText.setAttribute('font-size', finalSubtitleSize)
      }
    }
    // La contraportada no tiene texto editable
  }

  updateSpinePreview(text) {
    // Asegurarse de que la referencia al elemento existe
    if (!this.spineText) {
      this.spineText = document.getElementById('cover-spine-text')
    }
    
    if (this.spineText) {
      // Actualizar el texto (usar el valor del parámetro o el valor del input)
      const textValue = text !== undefined ? text : (this.spineInput ? this.spineInput.value : '')
      this.spineText.textContent = textValue || 'Texto de lomo'
      // Actualizar posición Y para que esté alineado desde el top del logo
      this.updateSpineTextPosition()
    } else {
      console.warn('CoverSelector: No se encontró el elemento cover-spine-text en el SVG')
    }
  }

  /**
   * Actualiza la posición del texto del lomo:
   * - Centrado en X dentro del contenedor del lomo
   * - A 25% desde abajo (Y)
   * - Rotado -90 grados
   * - El texto crece hacia arriba para no encimarse con el logo
   */
  updateSpineTextPosition() {
    if (!this.spineText) return
    
    // Obtener dimensiones del SVG
    const svg = this.spineText.closest('svg')
    if (!svg) return
    
    const viewBox = svg.getAttribute('viewBox')
    if (!viewBox) return
    
    // Calcular dimensiones usando FOTOBOOK_CONFIG si está disponible
    const pageWidth = this.fotobookConfig.pageWidth || 300
    const pageHeight = this.fotobookConfig.pageHeight || 300
    
    // Calcular ancho del lomo: 1 cm a 300 DPI
    const spineWidthPx = Math.round(1.0 * 300 / 2.54)
    
    // Posición X: centro del lomo (las coordenadas son relativas al grupo cover-spine-sheet-group)
    const textX = spineWidthPx - (spineWidthPx / 4)
    
    // Posición Y: 25% desde abajo del lomo
    const textY = pageHeight - Math.round(pageHeight * 0.15)
    
    // Actualizar posición y atributos
    // text-anchor="middle" centra el texto horizontalmente
    // dominant-baseline="bottom" alinea desde abajo para que el texto crezca hacia arriba
    this.spineText.setAttribute('x', textX)
    this.spineText.setAttribute('y', textY)
    this.spineText.setAttribute('text-anchor', 'center')
    this.spineText.setAttribute('dominant-baseline', 'center')
    
    // Rotar -90 grados alrededor del punto (textX, textY)
    // Como text-anchor="middle", el texto está centrado en X antes de rotar
    // Después de rotar, seguirá centrado en X
    this.spineText.setAttribute('transform', `rotate(-90 ${textX} ${textY})`)
  }

  updateTextPositions(cover) {
    const svgWidth = this.pageWidth
    const svgHeight = this.pageHeight

    // Calcular posición X del título según alineación
    const titleX = this.calculateTextX(cover.title_align || 'center', svgWidth)
    const titleAnchor = this.getTextAnchor(cover.title_align || 'center')
    const titleY = (cover.title_y || 0.3) * svgHeight
    
    // Calcular tamaño del título basado en title_size del CMS
    const titleSizeFromCMS = (cover.title_size || 0.08) * svgHeight
    
    // Calcular tamaño ajustado al safe area (90% del ancho, 5% de margen de cada lado)
    const titleSizeAdjusted = this.calculateTitleSizeForSafeArea(
      this.leftCoverTitleText,
      svgWidth,
      titleSizeFromCMS,
      cover.title_align || 'center'
    )
    
    // Usar el menor entre el tamaño del CMS y el tamaño ajustado al safe area
    const titleSize = Math.min(titleSizeFromCMS, titleSizeAdjusted)

    // Calcular posición X del subtítulo según alineación
    const subtitleX = this.calculateTextX(cover.subtitle_align || 'center', svgWidth)
    const subtitleAnchor = this.getTextAnchor(cover.subtitle_align || 'center')
    const subtitleY = (cover.subtitle_y || 0.4) * svgHeight
    const subtitleSizeFromCMS = (cover.subtitle_size || 0.06) * svgHeight

    // Actualizar título izquierdo en #svg-cover-details
    if (this.leftCoverTitleText) {
      this.leftCoverTitleText.setAttribute('x', titleX)
      this.leftCoverTitleText.setAttribute('y', titleY)
      this.leftCoverTitleText.setAttribute('text-anchor', titleAnchor)
      this.leftCoverTitleText.setAttribute('font-size', titleSize)
    }

    // Actualizar subtítulo izquierdo en #svg-cover-details
    if (this.leftCoverSubtitleText) {
      this.leftCoverSubtitleText.setAttribute('x', subtitleX)
      this.leftCoverSubtitleText.setAttribute('y', subtitleY)
      this.leftCoverSubtitleText.setAttribute('text-anchor', subtitleAnchor)
      
      // Calcular tamaño ajustado al safe area para el subtítulo
      const subtitleSizeAdjusted = this.calculateSubtitleSizeForSafeArea(
        this.leftCoverSubtitleText,
        svgWidth,
        subtitleSizeFromCMS,
        cover.subtitle_align || 'center'
      )
      const finalSubtitleSize = Math.min(subtitleSizeFromCMS, subtitleSizeAdjusted)
      this.leftCoverSubtitleText.setAttribute('font-size', finalSubtitleSize)
    }

    // La contraportada no tiene texto editable, solo el logo
    // Los elementos rightCoverTitleText y rightCoverSubtitleText no se usan
  }

  /**
   * Calcula el tamaño de fuente del título para que quepa en el safe area (90% del ancho)
   * @param {SVGTextElement} titleElement - Elemento de texto del título
   * @param {number} svgWidth - Ancho total del SVG
   * @param {number} initialSize - Tamaño inicial calculado desde title_size del CMS
   * @param {string} align - Alineación del texto ('left', 'center', 'right')
   * @returns {number} Tamaño de fuente ajustado
   */
  calculateTitleSizeForSafeArea(titleElement, svgWidth, initialSize, align) {
    if (!titleElement) return initialSize

    // Calcular safe area: 90% del ancho (5% de margen de cada lado)
    const safeAreaWidth = svgWidth * 0.9

    // Obtener el texto actual
    const text = titleElement.textContent || ''
    if (!text.trim()) return initialSize

    // Obtener la fuente actual del elemento
    const fontFamily = titleElement.getAttribute('font-family') || 'Arial, sans-serif'
    const fontWeight = titleElement.getAttribute('font-weight') || 'bold'

    // Crear un elemento temporal para medir el ancho del texto
    const tempText = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    tempText.setAttribute('font-family', fontFamily)
    tempText.setAttribute('font-weight', fontWeight)
    tempText.setAttribute('font-size', initialSize)
    tempText.textContent = text
    tempText.setAttribute('visibility', 'hidden')
    
    // Agregar temporalmente al SVG para poder medirlo
    const svg = titleElement.ownerSVGElement || this.svgCoverDetails
    if (!svg) return initialSize
    
    svg.appendChild(tempText)
    
    // Medir el ancho actual del texto
    const textWidth = tempText.getComputedTextLength()
    
    // Remover el elemento temporal
    svg.removeChild(tempText)

    // Si el texto ya cabe en el safe area, usar el tamaño inicial
    if (textWidth <= safeAreaWidth) {
      return initialSize
    }

    // Calcular el factor de escala necesario para que quepa en el safe area
    const scaleFactor = safeAreaWidth / textWidth
    
    // Aplicar el factor de escala al tamaño inicial
    const adjustedSize = initialSize * scaleFactor

    return adjustedSize
  }

  /**
   * Calcula el tamaño de fuente del subtítulo para que quepa en el safe area (90% del ancho)
   * @param {SVGTextElement} subtitleElement - Elemento de texto del subtítulo
   * @param {number} svgWidth - Ancho total del SVG
   * @param {number} initialSize - Tamaño inicial calculado desde subtitle_size del CMS
   * @param {string} align - Alineación del texto ('left', 'center', 'right')
   * @returns {number} Tamaño de fuente ajustado
   */
  calculateSubtitleSizeForSafeArea(subtitleElement, svgWidth, initialSize, align) {
    if (!subtitleElement) return initialSize

    // Calcular safe area: 90% del ancho (5% de margen de cada lado)
    const safeAreaWidth = svgWidth * 0.9

    // Obtener el texto actual
    const text = subtitleElement.textContent || ''
    if (!text.trim()) return initialSize

    // Obtener la fuente actual del elemento
    const fontFamily = subtitleElement.getAttribute('font-family') || 'Arial, sans-serif'
    const fontWeight = subtitleElement.getAttribute('font-weight') || 'normal'

    // Crear un elemento temporal para medir el ancho del texto
    const tempText = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    tempText.setAttribute('font-family', fontFamily)
    tempText.setAttribute('font-weight', fontWeight)
    tempText.setAttribute('font-size', initialSize)
    tempText.textContent = text
    tempText.setAttribute('visibility', 'hidden')
    
    // Agregar temporalmente al SVG para poder medirlo
    const svg = subtitleElement.ownerSVGElement || this.svgCoverDetails
    if (!svg) return initialSize
    
    svg.appendChild(tempText)
    
    // Medir el ancho actual del texto
    const textWidth = tempText.getComputedTextLength()
    
    // Remover el elemento temporal
    svg.removeChild(tempText)

    // Si el texto ya cabe en el safe area, usar el tamaño inicial
    if (textWidth <= safeAreaWidth) {
      return initialSize
    }

    // Calcular el factor de escala necesario para que quepa en el safe area
    const scaleFactor = safeAreaWidth / textWidth
    
    // Aplicar el factor de escala al tamaño inicial
    const adjustedSize = initialSize * scaleFactor

    return adjustedSize
  }

  calculateTextX(align, svgWidth) {
    switch (align) {
      case 'left':
        return svgWidth * 0.1
      case 'right':
        return svgWidth * 0.9
      default: // center
        return svgWidth * 0.5
    }
  }

  getTextAnchor(align) {
    switch (align) {
      case 'left':
        return 'start'
      case 'right':
        return 'end'
      default: // center
        return 'middle'
    }
  }

  markActiveChip(color) {
    if (!this.paletteContainer) return
    this.paletteContainer.querySelectorAll('[data-color]').forEach(chip => {
      if (chip.getAttribute('data-color') === color) {
        chip.classList.add('active')
      } else {
        chip.classList.remove('active')
      }
    })
  }

  parseColors(value) {
    if (!value) return []
    return value.split(',')
      .map(c => c.trim())
      .filter(c => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(c))
  }

  parseFonts(value) {
    if (!value) return []
    return value.split(',')
      .map(f => f.trim())
      .filter(f => f.length > 0)
  }

  hexToRgbCss(hexColor) {
    const rgb = this.hexToRgb(hexColor)
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
  }

  hexToRgb(hexColor) {
    let r, g, b
    if (hexColor.length === 4) {
      r = parseInt(hexColor[1] + hexColor[1], 16)
      g = parseInt(hexColor[2] + hexColor[2], 16)
      b = parseInt(hexColor[3] + hexColor[3], 16)
    } else {
      r = parseInt(hexColor.slice(1, 3), 16)
      g = parseInt(hexColor.slice(3, 5), 16)
      b = parseInt(hexColor.slice(5, 7), 16)
    }
    return { r, g, b }
  }

  getContrastColor(hexColor) {
    const { r, g, b } = this.hexToRgb(hexColor)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.5 ? '#000000' : '#FFFFFF'
  }
}

